import draftToHtml from 'draftjs-to-html';
import { FormikHelpers, FormikValues } from 'formik';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mixed, object } from 'yup';

import Cabecalho from 'Component/Admin/Cabecalho';
import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Form, { Editor } from 'Component/Form';
import Icone from 'Component/Icone';
import Imagem from 'Component/Imagem';
import Observacao from 'Interface/Observacoes';
import { useAppSelector } from 'Store';
import { useAdicionarObservacaoMutation } from 'View/Service/Default/Observacoes';
import {
  useEditarSolicitacaoMutation,
  useRecuperarSolicitacaoQuery,
} from 'View/Service/Default/Solicitacoes';

const regras = object({
  texto: mixed().test('obrigatorio', 'Obrigatório', (valor) => {
    if (valor?.blocks?.length && valor.blocks[0].text) {
      return true;
    }

    return false;
  }),
});

const Visualizar = (): React.ReactElement => {
  const { id = 0 } = useParams();
  const usuario = useAppSelector((estado) => estado.autenticacao.usuario);
  const toast = useToast();
  const [adicionarObservacao] = useAdicionarObservacaoMutation();
  const [editarSolicitacao] = useEditarSolicitacaoMutation();

  const { data: { solicitacao } = {}, refetch } = useRecuperarSolicitacaoQuery({
    id: id,
    contain: ['Clientes', 'Atendentes', 'Observacoes.Usuarios'],
    scope: false,
  });

  useEffect(() => {
    refetch();
  }, [refetch])

  const enviar = async (
    observacao: Observacao,
    form: FormikHelpers<FormikValues>
  ): Promise<void> => {
    try {
      await adicionarObservacao({
        ...observacao,
        solicitacao_id: id as number,
      }).unwrap();

      toast({
        tema: 'success',
        conteudo: 'Observação adicionada com sucesso!',
      });

      form.resetForm();
    } catch (erro) {
      toast({
        titulo: 'Erro ao adicionar observação',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const iniciar = async (): Promise<void> => {
    if (solicitacao && usuario) {
      try {
        await editarSolicitacao({
          id: solicitacao.id,
          status: 1,
          atendente_id: usuario.id,
        }).unwrap();

        toast({
          tema: 'success',
          conteudo: 'Solicitação finalizada com sucesso!',
        });
      } catch (erro) {
        toast({
          titulo: 'Erro ao finalizar a solicitação',
          conteudo: 'Verifique os logs para maiores detalhes.',
          tema: 'danger',
        });
      }
    }
  };

  const finalizar = async (): Promise<void> => {
    if (solicitacao) {
      try {
        await editarSolicitacao({
          id: solicitacao.id,
          status: 2,
        }).unwrap();

        toast({
          tema: 'success',
          conteudo: 'Solicitação finalizada com sucesso!',
        });
      } catch (erro) {
        toast({
          titulo: 'Erro ao finalizar a solicitação',
          conteudo: 'Verifique os logs para maiores detalhes.',
          tema: 'danger',
        });
      }
    }
  };

  return (
    <div className="animate__animated animate__fadeIn">
      <Cabecalho
        titulo={`Visualizando solicitação #${solicitacao?.id}`}
        descricao={solicitacao?.titulo}
      />
      <div className="row justify-content-center">
        <div className="col-sm-3">
          <div className="bg-white shadow-sm rounded p-3">
            <div className="d-flex justify-content-center align-items-center flex-column">
              <Link
                to={`/admin/usuarios/editar/${solicitacao?.cliente?.id}`}
                className="text-decoration-none"
                target="_blank"
              >
                <div className="position-relative">
                  <Imagem
                    src={solicitacao?.cliente?.imagem}
                    width="100"
                    height="100"
                    className="rounded-circle object-fit-cover shadow-lg border border-white border-3"
                  />
                  <div
                    className="bg-primary shadow text-white rounded-circle position-absolute top-0 end-0 d-flex justify-content-center align-items-center"
                    style={{ width: 30, height: 30 }}
                  >
                    <Icone nome="box-arrow-up-right" />
                  </div>
                </div>
              </Link>
              <p className="mt-3 text-center">
                <Link
                  to={`/admin/usuarios/editar/${solicitacao?.cliente?.id}`}
                  className="text-decoration-none"
                  target="_blank"
                >
                  <strong>{solicitacao?.cliente?.nome}</strong>
                </Link>
                <br />
                <small className="d-block">
                  <a
                    href={`mail:${solicitacao?.cliente?.email}`}
                    className="text-muted text-decoration-none"
                  >
                    <Icone nome="envelope" className="me-2" />
                    {solicitacao?.cliente?.email}
                  </a>
                </small>
                <small className="text-muted d-block">
                  <a
                    href={`https://api.whatsapp.com/send?phone=55${solicitacao?.cliente?.telefone1
                      .replace('(', '')
                      .replace(')', '')
                      .replace('-', '')
                      .replace(' ', '')}`}
                    className="text-muted text-decoration-none"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icone nome="whatsapp" className="me-2" />
                    {solicitacao?.cliente?.telefone1}
                  </a>
                </small>
              </p>
            </div>
            {solicitacao?.atendente && (
              <p>
                Atendimento por
                <br />
                <strong className="fs-4">{solicitacao?.atendente?.nome}</strong>
              </p>
            )}
            {solicitacao?.iniciado && (
              <p>
                Iniciado em
                <br />
                <strong>
                  {solicitacao?.iniciado
                    .data()
                    .toLocaleString(DateTime.DATETIME_SHORT)}
                </strong>
              </p>
            )}
            {solicitacao?.status == 2 && (
              <p className="mb-0">
                Finalizado em
                <br />
                <strong>
                  {solicitacao.finalizado
                    .data()
                    .toLocaleString(DateTime.DATETIME_SHORT)}
                </strong>
              </p>
            )}
            {solicitacao?.status == 0 && (
              <Botao
                tema="success"
                texto="Iniciar atendimento"
                className="w-100"
                onClick={iniciar}
                confirmacao="Você tem certeza que deseja iniciar o atendimento?"
              />
            )}
            {solicitacao?.status == 1 && (
              <Botao
                texto="Finalizar"
                className="w-100"
                onClick={finalizar}
                confirmacao="Você tem certeza que deseja finalizar essa solicitação?"
              />
            )}
          </div>
        </div>
        <div className="col-sm-9 mt-3 mt-sm-0">
          <div className="bg-white shadow-sm rounded p-3 position-relative">
            {solicitacao && (
              <div className="mb-1 bg-light rounded p-3 border">
                <div
                  dangerouslySetInnerHTML={{
                    __html: draftToHtml(solicitacao.descricao),
                  }}
                />
                <small>
                  Por <strong>{solicitacao.cliente?.nome}</strong> as{' '}
                  <strong>
                    {solicitacao.criado
                      .data()
                      .toLocaleString(DateTime.DATETIME_SHORT)}
                  </strong>
                </small>
              </div>
            )}
            {solicitacao?.observacoes?.map((observacao) => (
              <div
                key={`${observacao.id}-${observacao.modificado}`}
                className={`mb-1 rounded p-3 ${
                  observacao.usuario_id == solicitacao.cliente_id
                    ? 'bg-light border me-5'
                    : 'bg-primary bg-opacity-25 ms-5'
                }`}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: draftToHtml(observacao.texto),
                  }}
                />
                <small>
                  Por <strong>{observacao.usuario?.nome}</strong> as{' '}
                  <strong>
                    {observacao.criado
                      .data()
                      .toLocaleString(DateTime.DATETIME_SHORT)}
                  </strong>
                </small>
              </div>
            ))}
            <div className="mt-5 mb-n3 position-relative">
              <Form
                onSubmit={enviar}
                regras={regras}
                desabilitado={solicitacao?.status != 1}
              >
                <Editor
                  name="texto"
                  rotulo="Observações"
                  readOnly={solicitacao?.status != 1}
                />
                <Botao
                  lg
                  circulo
                  type="submit"
                  icone="send"
                  className="position-absolute top-0 end-0 translate-middle"
                />
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Visualizar;
